import * as XLSX from "xlsx";

export interface ParsedSheet {
  sheetNames: string[];
  activeSheet: string;
  matrix: string[][]; // full sheet content (trimmed of trailing empty rows, padded to numCols)
  numCols: number;
  suggestedHeaderRow: number; // first row with >= 2 non-empty cells (best guess)
}

export async function parseIRLFile(file: File, sheetName?: string): Promise<ParsedSheet> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  const sheetNames = workbook.SheetNames;
  if (sheetNames.length === 0) {
    throw new Error("No sheets found in file");
  }

  const activeSheet =
    sheetName && sheetNames.includes(sheetName) ? sheetName : sheetNames[0];
  const sheet = workbook.Sheets[activeSheet];

  const rawMatrix = (XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: false,
  }) as unknown[][]).map((row) =>
    (row || []).map((cell) => String(cell ?? "").trim())
  );

  // Trim trailing empty rows
  let lastNonEmpty = rawMatrix.length - 1;
  while (lastNonEmpty >= 0 && rawMatrix[lastNonEmpty].every((c) => c === "")) {
    lastNonEmpty--;
  }
  const trimmed = rawMatrix.slice(0, lastNonEmpty + 1);

  // Pad rows to a consistent column count
  const numCols = trimmed.reduce((max, r) => Math.max(max, r.length), 0);
  const matrix = trimmed.map((r) => {
    const padded = [...r];
    while (padded.length < numCols) padded.push("");
    return padded;
  });

  // Best-guess header row: first row in the first 15 with >= 2 non-empty cells
  let suggestedHeaderRow = 0;
  for (let i = 0; i < Math.min(matrix.length, 15); i++) {
    const nonEmpty = matrix[i].filter((c) => c !== "").length;
    if (nonEmpty >= 2) {
      suggestedHeaderRow = i;
      break;
    }
  }

  return { sheetNames, activeSheet, matrix, numCols, suggestedHeaderRow };
}
